import { Action } from 'typescript-fsa';
import * as actions from './actions';
import { Dispatch } from 'redux';
import { Storage } from 'aws-amplify';
import { fetchKansatsuKirokuSubdiv, getKansatsuKirokuSubdiv, postKansatsuKirokuSubdiv, uploadImage, getArchiveStatusSubdiv, getLatestKansatsuKirokuSubdiv } from '../../../apis';
import { loadingOperations } from '../loading';
import { getLogger } from '../../../utils';
import { systemStateMessageOperations } from '..';
import { KansatsuKirokuSubdiv } from '../../../models/KansatsuKirokuSubdiv';

const getSignedUrl = async (params: { bucket: string; key: string;}) => {
  return Storage.vault.get(params.key,
    {
      bucket: params.bucket,
      level: 'public',
      customPrefix: {
        public: ''
      }
    });
};

const fetch = ({ koujiBangou }
      : { koujiBangou: string }) => async (dispatch: Dispatch<Action<any>>) => {
  loadingOperations.startLoading()(dispatch);

  try {
    const result = await fetchKansatsuKirokuSubdiv({ koujiBangou });

    // ArchiveStatusを取得するAPIをループで呼び出す
    const archiveStatus = async (record: KansatsuKirokuSubdiv): Promise<null> => {
      const archiveStatusResult = await getArchiveStatusSubdiv({ koujiBangou: record.koujiBangou, key: record.key });
      // 検索結果を観察記録のレコードに設定する
      record.downloadFileStatus = archiveStatusResult.status;
      record.downloadFileBucket = archiveStatusResult.bucket;
      record.downloadFileKey = archiveStatusResult.fileKey;
      record.downloadFileCreationDatetime = archiveStatusResult.createdAt;
      return null;
    };
    await Promise.all(result.Items.map(r => archiveStatus(r)));

    dispatch(actions.fetchRecords.done({ params: { koujiBangou }, result }));
  } catch (error) {
    dispatch(actions.fetchRecords.failed({ params: { koujiBangou }, error: error.toString() }));
    getLogger().error('kansatsuKirokuSubdiv/fetch()', error);
    systemStateMessageOperations.putError({ message: '通信エラーが発生しました。' })(dispatch);
  } finally {
    loadingOperations.stopLoading()(dispatch);
  }
}

const get = ({ koujiBangou, key }
      : { koujiBangou: string; key:string }) => async (dispatch: Dispatch<Action<any>>) => {
  loadingOperations.startLoading()(dispatch);

  try {
    const result = await getKansatsuKirokuSubdiv({ koujiBangou, key });
    // 画像の情報があればSignedURLを生成して返す
    if (result.croppedImageBucket && result.croppedImageKey) {
      result.croppedImageSignedUrl =
        await Storage.get(
          result.croppedImageKey,
          {
            customPrefix: {
              public: ''
            }
          }) as string
    }

    // 推論結果に含まれる画像のPresigedURLを取得して設定する
    if (result.inferenceResultSubdiv) {
      await Promise.all(result.inferenceResultSubdiv.map(
        async (item) => {
          const url = await getSignedUrl({ key: item.overlay_img_key, bucket: item.overlay_img_bucket });
          item.overlay_img_presigned_url = url as string;
        }
      ));
    }
    dispatch(actions.getRecord.done({ params: { koujiBangou, key }, result: { Item: result }}));
  } catch (error) {
    dispatch(actions.getRecord.failed({ params: { koujiBangou, key }, error: error.toString() }));
    getLogger().error('kansatsuKirokuSubdiv/get()', error);
    systemStateMessageOperations.putError({ message: '通信エラーが発生しました。' })(dispatch);
  } finally {
    loadingOperations.stopLoading()(dispatch);
  }
}
const post = (
      record : KansatsuKirokuSubdiv,
      originalImageStr: string | undefined = undefined,
      originalImageContentType: string | undefined = undefined
    ) => async (dispatch: Dispatch<Action<any>>) => {
  loadingOperations.startLoading()(dispatch);

  try {
    // オリジナル画像が指定されているときはアップロードする
    if (originalImageStr && originalImageContentType) {
      // 拡張子をcontentTypeから取得する
      const ext = originalImageContentType.toLowerCase().endsWith('png')
              ? 'png'
              : 'jpeg'
      // ファイル名生成
      const key = `${record.inferenceId}.${ext}`;
      await uploadImage({ key, imageStr: originalImageStr, contentType: originalImageContentType });
      record.originalImageBucket = process.env.REACT_APP_IMAGEUPLOADBUCKETNAME || '';
      record.originalImageKey = key;
    }
    await postKansatsuKirokuSubdiv({ item: record });
    dispatch(actions.postRecord.done({ params: { item: record }, result: { item: record }}));
    // 削除日時が指定されているかどうかで結果メッセージを変更
    const message = record.deletedAt ? '削除しました。' : '保存しました。';
    systemStateMessageOperations.putSuccess({ message })(dispatch);
  } catch (error) {
    dispatch(actions.postRecord.failed({ params: { item: record }, error: error.toString() }));
    getLogger().error('kansatsuKirokuSubdiv/post()', error);
    systemStateMessageOperations.putError({ message: '通信エラーが発生しました。' })(dispatch);
  } finally {
    loadingOperations.stopLoading()(dispatch);
  }
}

const resetRecord = () => async (dispatch: Dispatch<Action<any>>) => {
  dispatch(actions.resetRecord());
};

// 最新の岩石グループを取得するオペレーション
const fetchLatestRockMassGroup = ({ koujiBangou }: { koujiBangou: string }) => async (dispatch: Dispatch<Action<any>>): Promise<{ rockMassGroup: string | undefined } | undefined> => {
  // ローディング開始 (必要に応じて)
  // loadingOperations.startLoading()(dispatch);

  try {
    dispatch(actions.getLatestRockMassGroup.started({ koujiBangou }));

    // APIを呼び出して最新のレコード情報を取得
    // APIクライアントは文字列を直接返すことを期待
    const resultString = await getLatestKansatsuKirokuSubdiv({ koujiBangou });

    let rockMassGroup: string | undefined;
    if (typeof resultString === 'string' && resultString !== '') {
       rockMassGroup = resultString;
    }

    dispatch(actions.getLatestRockMassGroup.done({
      params: { koujiBangou },
      result: { rockMassGroup }
    }));

    // ↓ 取得した値を含むオブジェクトを return する
    return { rockMassGroup }; // Component 側で待つ Promise の解決値

  } catch (error) {
    const errorMessage = error.toString();
    dispatch(actions.getLatestRockMassGroup.failed({ params: { koujiBangou }, error: errorMessage }));
    getLogger().error('kansatsuKirokuSubdiv/fetchLatestRockMassGroup()', error);
    // 必要に応じてエラーメッセージを表示
    // systemStateMessageOperations.putError({ message: '最新の岩石区分の取得に失敗しました。' })(dispatch);
    // ↓ catch ブロックでも明示的に値を返す (undefined)
    return undefined; // エラー時は undefined を返す
  } finally {
    // ローディング終了 (必要に応じて)
    // loadingOperations.stopLoading()(dispatch);
  }
};

export {
  fetch,
  get,
  post,
  resetRecord,
  fetchLatestRockMassGroup
};



// WEBPACK FOOTER //
// ./src/redux/modules/kansatsuKirokuSubdiv/operations.ts